import Image from 'next/image'
import ReactModal, { Styles } from 'react-modal'
import uri from '../../../public/img/new_tech_home.jpg'
import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import toast from 'react-hot-toast'
import { Button } from '../Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { CustomFormControl, Label } from '../Fields/CustomTextField'
import { nestBackendClient } from '../clients/nest-backend.client'
import Link from 'next/link'
import { useQueryParam } from '../../hooks/useQueryParam'

const validationSchema = Yup.object().shape({
  accept: Yup.boolean().required('you must accept').isTrue('you must accept'),
})

interface Props {
  phoneNumber: string
  isOpen: boolean
  onRequestClose?: () => void
}

export const DiscountModal = ({ isOpen, onRequestClose, phoneNumber }: Props) => {
  const query = useQueryParam()
  const modalStyle: Styles = {
    content: {
      flexDirection: 'column',
      padding: 0,
      height: 'min-content',
      maxWidth: '400px',
      overflow: 'visible',
      margin: 'auto',
    },
    overlay: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `rgba(0, 0, 0, 0.75)`,
      zIndex: 100,
    },
  }

  async function onSubmit(values) {
    if (!values.accept) return
    try {
      await nestBackendClient.post('/contacts/allow-campaign-messages', {
        phone: phoneNumber,
        discountPromo: true,
      })
      onRequestClose()
      toast('Thank you, preferences updated!', {
        duration: 4000,
        icon: '✅',
      })
    } catch (error) {
      toast(error.response?.data?.message ?? error.message, {
        icon: '❌',
        duration: 2000,
      })
    }
  }

  return (
    <ReactModal style={modalStyle} isOpen={isOpen} onRequestClose={onRequestClose}>
      <button
        onClick={onRequestClose}
        className="absolute z-10 -top-5 -right-5 flex items-center justify-center h-10 w-10 rounded-full bg-white"
      >
        <span className=" text-[#f04f23] border-[#f04f23] border-2 rounded-full h-5 w-5 flex items-center justify-center">
          &#x2715;
        </span>
      </button>
      <div className="w-full overflow-hidden flex items-center flex-col">
        <div className="w-[126%]">
          <Image
            src={uri}
            priority={true}
            alt="NuBrakes Header Van Image"
            placeholder="blur"
            objectFit="cover"
            objectPosition="bottom center"
          />
        </div>
      </div>
      <div className="p-2 flex flex-col items-center">
        <h2 className="w-52 text-center">Get 5% Off Your Quote</h2>
        <Formik
          validationSchema={validationSchema}
          initialValues={{ accept: false }}
          enableReinitialize={false}
          isInitialValid={false}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValid, handleSubmit }) => (
            <Form className="flex flex-col w-full max-w-lg p-3" onSubmitCapture={handleSubmit}>
              <div className="mb-3 flex items-center">
                <Field
                  name={'accept'}
                  componet={CustomFormControl}
                  type="checkbox"
                  id="accept-check"
                  className="w-10 h-10 border-2 rounded-lg"
                />
                <Label className="ml-2" htmlFor="accept-check">
                  <p className="font-normal text-[9px] mb-0">
                    <strong>Nubrakes</strong> By checking this box, you agree to receive promotional messages sent via
                    an autodialer to the phone number you provided on the prior page. You also agree to the{' '}
                    <Link href={{ pathname: `/terms-conditions`, query }}>
                      <a>Terms of Service</a>
                    </Link>{' '}
                    and <Link href={{ pathname: `/privacy-policy`, query }}><a>Privacy Policy</a></Link>. Consent isn’t a condition of any purchase. Message frequency varies.
                    Message and data rates may apply. Offer cannot be combined with other promotions; however, the highest eligible discount will be applied. Reply STOP to opt out; HELP for more information.
                  </p>
                </Label>
              </div>
              <Button
                className="w-full py-3 uppercase"
                disabled={isSubmitting || !isValid}
                type={'submit'}
                id={'submit-btn'}
              >
                {isSubmitting ? (
                  <FontAwesomeIcon icon={faSpinner} spin={true} />
                ) : (
                  <>
                    <h4 className="m-0">Get 5% Off</h4>
                    <span className="text-sm">When You Sign Up For Texts</span>
                  </>
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </ReactModal>
  )
}
